import type { fabric } from 'fabric';
import React, { useCallback, useMemo } from 'react';
import {
  PLACEHOLDER_KEY_REGISTRY,
  getKeysForKind,
  isKeyCompatibleWithKind,
  isMultiMatchTemplateType,
  missingFieldsFor,
  needsMatchNumber,
  usedIdentityFieldsFor,
} from 'modules/manager/domain/placeholder/placeholder-key-registry';
import { isKnownPlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import type { PlaceholderData, PlaceholderKind } from 'modules/manager/domain/placeholder/types';
import { getTemplateTypeEntry } from 'modules/manager/domain/template-type';
import type { ObjectInspectorProps } from '../types';
import CommitOptionalNumberField from './commit-optional-number-field';
import CommitTextField from './commit-text-field';
import HelpBubble from './help-bubble';
import InspectorRow from './inspector-row';
import InspectorSection from './inspector-section';
import SelectField from './select-field';
import { useCommonFabricData } from './use-common-fabric-data';

const UNSET_OPTION = { value: '', label: 'Не задано' };
const MIXED_OPTION = { value: '__mixed__', label: '(разное)', disabled: true };
const MIXED_KIND_OPTION = {
  value: '__mixed__',
  label: '(разные типы — откройте по одному)',
  disabled: true,
};

const PlaceholderIdentitySection: React.FC<ObjectInspectorProps> = ({ objects, editor }) => {
  const key = useCommonFabricData<string>(objects, 'placeholderKey', editor);
  const label = useCommonFabricData<string>(objects, 'placeholderLabel', editor);
  const teamNumber = useCommonFabricData<number>(objects, 'teamNumber', editor);
  const playerNumber = useCommonFabricData<number>(objects, 'playerNumber', editor);
  const matchNumber = useCommonFabricData<number>(objects, 'matchNumber', editor);

  const templateType = editor.placeholders?.getTemplateType();

  // Which identity fields the current key actually reads on this template type.
  // A mixed selection passes `undefined` so it takes the same "unknown key"
  // fallback the helper already implements — that rule lives in one place, and
  // it correctly omits «Команда №» on a type that has no team axis.
  const usedFields = useMemo(
    () => usedIdentityFieldsFor(key.isMixed ? undefined : key.value, templateType),
    [key.isMixed, key.value, templateType],
  );
  const showTeamNumber = usedFields.has('teamNumber');
  const showPlayerNumber = usedFields.has('playerNumber');
  const showMatchNumber = key.isMixed ? isMultiMatchTemplateType(templateType) : needsMatchNumber(key.value, templateType);

  const refitAll = useCallback(() => {
    for (const o of objects) editor.placeholders?.refit(o as fabric.Group);
  }, [editor, objects]);

  const sharedKind: PlaceholderKind | undefined = useMemo(() => {
    if (objects.length === 0) return undefined;
    const first = (objects[0].data as { kind?: PlaceholderKind } | undefined)?.kind;
    if (!first) return undefined;
    return objects.every((o) => (o.data as { kind?: PlaceholderKind } | undefined)?.kind === first) ? first : undefined;
  }, [objects]);

  const keyOptions = useMemo(() => {
    if (!sharedKind) return [MIXED_KIND_OPTION];
    const kindKeys = getKeysForKind(sharedKind);
    // Restrict to the keys the active template type actually resolves — e.g.
    // a TOURNAMENT_TABLE template only offers standing/team/tournament keys,
    // a match template no longer offers table-statistics keys. Unknown
    // template type (still loading) falls back to showing every kind-
    // compatible key so the dropdown isn't empty while data loads.
    const allowedKeys = templateType ? getTemplateTypeEntry(templateType).allowedPlaceholderKeys : undefined;
    const base = kindKeys
      .filter((value) => !allowedKeys || allowedKeys.has(value))
      .map((value) => ({ value, label: PLACEHOLDER_KEY_REGISTRY[value].label }));
    const opts = [UNSET_OPTION, ...base];
    if (key.isMixed) return [MIXED_OPTION, ...opts];
    if (key.value && !isKnownPlaceholderKey(key.value)) {
      return [{ value: key.value, label: `(unknown: ${key.value})`, disabled: true }, ...opts];
    }
    if (key.value && !isKeyCompatibleWithKind(key.value, sharedKind)) {
      return [{ value: key.value, label: `(wrong type: ${key.value})`, disabled: true }, ...opts];
    }
    return opts;
  }, [sharedKind, key.value, key.isMixed, templateType]);

  // Missing-field highlighting is only meaningful for a single placeholder.
  const missing = useMemo(() => {
    if (objects.length !== 1) return new Set<string>();
    const data = (objects[0].data ?? {}) as Partial<PlaceholderData> & { placeholderKey?: string };
    return new Set(
      missingFieldsFor(
        {
          ...data,
          placeholderKey: key.value ?? '',
          teamNumber: teamNumber.value,
          playerNumber: playerNumber.value,
          matchNumber: matchNumber.value,
        } as PlaceholderData,
        templateType,
      ),
    );
  }, [objects, key.value, teamNumber.value, playerNumber.value, matchNumber.value, templateType]);

  const keyDisplayValue = key.isMixed ? '__mixed__' : (key.value ?? '');

  return (
    <InspectorSection title="Идентификация">
      <InspectorRow label="Ключ">
        <SelectField
          value={keyDisplayValue}
          onChange={(v) => {
            if (v === '__mixed__' || !sharedKind) return;
            key.setValue(v);
            refitAll();
          }}
          options={keyOptions}
        />
      </InspectorRow>
      <InspectorRow label="Подпись">
        <CommitTextField
          value={label.isMixed ? '' : label.value}
          placeholder={label.isMixed ? '(разное)' : 'Понятная подпись'}
          onCommit={(v) => {
            label.setValue(v);
            refitAll();
          }}
        />
      </InspectorRow>
      {showTeamNumber && (
        <InspectorRow label="Команда №" highlight={missing.has('teamNumber')}>
          <CommitOptionalNumberField
            value={teamNumber.isMixed ? undefined : teamNumber.value}
            placeholder={teamNumber.isMixed ? '(разное)' : undefined}
            onCommit={(v) => {
              teamNumber.setValue(v);
              refitAll();
            }}
            min={1}
          />
        </InspectorRow>
      )}
      {showPlayerNumber && (
        <InspectorRow label="Игрок №" highlight={missing.has('playerNumber')}>
          <CommitOptionalNumberField
            value={playerNumber.isMixed ? undefined : playerNumber.value}
            placeholder={playerNumber.isMixed ? '(разное)' : undefined}
            onCommit={(v) => {
              playerNumber.setValue(v);
              refitAll();
            }}
            min={1}
          />
        </InspectorRow>
      )}
      {showMatchNumber && (
        <InspectorRow label="Матч №" highlight={missing.has('matchNumber')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', minWidth: 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <CommitOptionalNumberField
                value={matchNumber.isMixed ? undefined : matchNumber.value}
                placeholder={matchNumber.isMixed ? '(разное)' : undefined}
                onCommit={(v) => {
                  matchNumber.setValue(v);
                  refitAll();
                }}
                min={1}
              />
            </div>
            <HelpBubble
              content={
                <span>
                  К какому матчу принадлежит этот плейсхолдер. <code>1</code> = первый матч из /render, <code>2</code> = второй и т.д.
                  Используется только для мульти-матч шаблонов (например, «Несколько матчей»).
                </span>
              }
            />
          </div>
        </InspectorRow>
      )}
    </InspectorSection>
  );
};

export default PlaceholderIdentitySection;
